// src/pages/WritePost/index.jsx
import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Form, 
  Input, 
  Button, 
  Card, 
  Divider, 
  Upload, 
  message, 
  Row, 
  Col,
  InputNumber,
  Typography
} from 'antd';
import { 
  SaveOutlined, 
  CloseOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../../components/header';
import { getPostById, createPost, updatePost } from '../../api/mockData';
import './index.css';

const { TextArea } = Input;
const { Title } = Typography;

const WritePost = ({ loggedInUser, onLogout }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); // 获取文章ID用于编辑
  const [isEditing, setIsEditing] = useState(false);
  const [postToEdit, setPostToEdit] = useState(null);

  // 检查是否为编辑模式
  useEffect(() => {
    if (id) {
      setIsEditing(true);
      fetchPostData();
    }
  }, [id]);

  const fetchPostData = async () => {
    try {
      const postData = await getPostById(id);
      if (postData) {
        setPostToEdit(postData);
        // 设置表单初始值
        form.setFieldsValue({
          title: postData.title,
          summary: postData.summary,
          content: postData.content,
          tags: postData.tags ? postData.tags.join(',') : '',
          views: postData.views || 0
        });
      }
    } catch (error) {
      message.error('获取文章数据失败: ' + error.message);
    }
  };

  // 处理表单提交
  const onFinish = async (values) => {
    setLoading(true);
    try {
      // 处理标签 - 将字符串转换为数组
      const tagsArray = values.tags ? values.tags.split(',').map(tag => tag.trim()) : [];
      
      // 准备文章数据
      const postData = {
        ...values,
        tags: tagsArray,
        author: loggedInUser.username,
        date: isEditing ? postToEdit.date : new Date().toISOString(), // 编辑时保留原日期
        id: isEditing ? parseInt(id) : undefined // 编辑时使用现有ID
      };

      let result;
      if (isEditing) {
        // 更新现有文章
        result = await updatePost(postData);
        if (result.success) {
          message.success('文章更新成功');
        }
      } else {
        // 创建新文章
        result = await createPost(postData);
        message.success('文章发布成功');
      }

      if (result.success) {
        form.resetFields();
        // 跳转到首页或文章详情页
        navigate('/');
      } else {
        message.error(result.message || '操作失败');
      }
    } catch (error) {
      message.error('发布失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 处理表单提交失败
  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  // 自定义标签上传
  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('只能上传 JPG/PNG 格式的图片!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB!');
    }
    return isJpgOrPng && isLt2M;
  };

  return (
    <Layout className="blog-layout">
      <AppHeader loggedInUser={loggedInUser} onLogout={onLogout}/>
      
      <Content className="writepost-content">
        <div className="writepost-container">
          <Card className="writepost-card">
            <div className="writepost-header">
              <h1 className="writepost-title">
                {isEditing ? '编辑文章' : '撰写新文章'}
              </h1>
              <p className="writepost-subtitle">
                {isEditing 
                  ? '修改现有文章内容' 
                  : '分享你的想法和经验'}
              </p>
            </div>
            
            <Divider />
            
            <Form
              form={form}
              name="writePost"
              labelCol={{ span: 3 }}
              wrapperCol={{ span: 20 }}
              initialValues={{ 
                remember: true,
                tags: postToEdit ? postToEdit.tags.join(',') : '',
                views: 0
              }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              className="writepost-form"
            >
              <Form.Item
                label="文章标题"
                name="title"
                rules={[
                  { required: true, message: '请输入文章标题!' },
                  { max: 100, message: '标题长度不能超过100个字符!' }
                ]}
              >
                <Input 
                  placeholder="请输入文章标题" 
                  size="large"
                  allowClear
                />
              </Form.Item>

              <Form.Item
                label="文章摘要"
                name="summary"
                rules={[
                  { required: true, message: '请输入文章摘要!' },
                  { max: 200, message: '摘要长度不能超过200个字符!' }
                ]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="请输入文章摘要，这将显示在文章卡片中" 
                  maxLength={200}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="文章内容"
                name="content"
                rules={[{ required: true, message: '请输入文章内容!' }]}
              >
                <TextArea 
                  rows={12} 
                  placeholder="开始撰写你的文章..." 
                  maxLength={10000}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="标签"
                name="tags"
                extra="多个标签请用逗号分隔，例如：React,JavaScript,前端"
              >
                <Input 
                  placeholder="请输入标签，用逗号分隔" 
                  allowClear
                />
              </Form.Item>

              <Form.Item
                label="封面图片"
                name="coverImage"
              >
                <Upload
                  name="cover"
                  listType="picture-card"
                  className="cover-uploader"
                  showUploadList={true}
                  beforeUpload={beforeUpload}
                  maxCount={1}
                >
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>上传封面</div>
                  </div>
                </Upload>
              </Form.Item>

              <Form.Item
                label="阅读量"
                name="views"
                initialValue={0}
              >
                <InputNumber 
                  min={0} 
                  placeholder="阅读量" 
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item wrapperCol={{ offset: 3, span: 20 }}>
                <Row gutter={16}>
                  <Col>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      size="large"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      {isEditing ? '更新文章' : '发布文章'}
                    </Button>
                  </Col>
                  <Col>
                    <Button 
                      size="large"
                      onClick={() => navigate('/')}
                      icon={<CloseOutlined />}
                    >
                      取消
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default WritePost;